import { EERIE } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIButton from 'UIComponents/button/UIButton';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITable from 'UIComponents/table/UITable';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { ApiNameToProductTranslationKeyMap } from 'pricing-pages-ui/constants/ApiNameToProductTranslationKeyMap';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { HUBSPOT_FREE } from 'self-service-api/constants/ApiNames';
import { CALL_TRANSCRIPTION_AND_COACHING, CONVERSATION_INTELLIGENCE } from 'self-service-api/constants/Features';
import { FREE, PROFESSIONAL, STARTER } from 'self-service-api/constants/ProductLevels';
import { SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { getProductLevelFromApiNameString } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getFeaturesByProductLineSelector } from '../../selectors/FeatureSelectors';
import { hasPricingAndPurchasingSucceededSelector, hasProductsSucceededSelector } from '../../selectors/LoadingStateSelectors';
import { daysSinceProductStartDateFromProductInventorySelector, highestProductLevelFromProductInventorySelector } from '../../selectors/ProductInventorySelectors';
import { Section } from '../common/SharedComponents';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import FeatureTableCol from './FeatureTableCol';
import FeatureTableColTag from './FeatureTableColTag';
import FeatureTablePriceDisplay from './FeatureTablePriceDisplay';
import FeatureTableRow from './FeatureTableRow';
import { GradientOverlay, TBody, THead } from './SharedComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const FeatureTable = ({
  productLine,
  highlightedProducts,
  screen,
  tab
}) => {
  const [expanded, setExpanded] = useState(false);
  const features = useSelector(state => {
    return getFeaturesByProductLineSelector(state, {
      productLine
    });
  });

  /**
   * Ticket: https://git.hubteam.com/HubSpot/pricing-page-requests/issues/440
   *
   * Temporary solution - Rearranges the SAF table, such that:
   * - call transcription and coaching is the last of Pro+Ent features
   * - conversation intelligence is the first of Ent features
   *
   * TODO: follow up on ticket
   */
  const sortedFeatures = useMemo(() => {
    if (productLine === SALES || productLine === SERVICE) {
      return [...features].sort((a, b) => {
        // a and b are from same group based on productApiNames property
        if (a.productApiNames.length === b.productApiNames.length && a.productApiNames.every(apiName => b.productApiNames.includes(apiName)) && b.productApiNames.every(apiName => a.productApiNames.includes(apiName))) {
          // conversation intelligence is the first Ent feature
          if (a.shortName === CONVERSATION_INTELLIGENCE) return -1;
          if (b.shortName === CONVERSATION_INTELLIGENCE) return 1;
          // call transcription and coaching is the last Pro + Ent feature
          if (a.shortName === CALL_TRANSCRIPTION_AND_COACHING) return 1;
          if (b.shortName === CALL_TRANSCRIPTION_AND_COACHING) return -1;
        }
        return 0;
      });
    }
    return features;
  }, [productLine, features]);
  const hasPricingAndPurchasingSucceeded = useSelector(hasPricingAndPurchasingSucceededSelector);
  const hasProductsSucceeded = useSelector(hasProductsSucceededSelector);
  const productLevelOwned = useSelector(state => {
    if (!hasPricingAndPurchasingSucceeded) return null;
    return highestProductLevelFromProductInventorySelector(state, {
      productLine
    });
  });
  const numOfDaysOwned = useSelector(state => {
    if (!productLevelOwned) return 0;
    return daysSinceProductStartDateFromProductInventorySelector(state, {
      productAttributeFilters: {
        productLine,
        productLevel: productLevelOwned
      }
    });
  });
  const recommendedProductLevel = PROFESSIONAL;
  const showRecommended = productLevelOwned === STARTER && numOfDaysOwned > 30;
  const handleExpandClick = () => {
    getUsageTracker().track('featureTableInteraction', {
      action: `clicked-${expanded ? 'collapse' : 'expand'}-table`,
      productLine,
      products: highlightedProducts,
      currentPlan: productLevelOwned,
      recommendedPlan: recommendedProductLevel,
      showRecommended,
      screen,
      tab
    });
    setExpanded(prev => !prev);
  };
  if (!features.length) return null;
  return /*#__PURE__*/_jsxs(Section, {
    children: [/*#__PURE__*/_jsx(GradientOverlay, {
      expanded: expanded,
      children: /*#__PURE__*/_jsxs(UITable, {
        bordered: false,
        hover: false,
        className: "m-top-8",
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {}), highlightedProducts.map(apiName => {
            return /*#__PURE__*/_jsx(FeatureTableCol, {
              apiName: apiName,
              productLine: productLine,
              recommendedProductLevel: recommendedProductLevel,
              showRecommended: showRecommended
            }, apiName);
          })]
        }), /*#__PURE__*/_jsx(THead, {
          children: /*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "upgrades.productCatalog.tableHeader.features"
              })
            }), highlightedProducts.map(apiName => {
              const apiNameForPrice = getProductLevelFromApiNameString(apiName) === FREE ? HUBSPOT_FREE : apiName;
              return /*#__PURE__*/_jsxs("th", {
                scope: "col",
                children: [/*#__PURE__*/_jsx(FeatureTableColTag, {
                  apiName: apiName,
                  productLine: productLine,
                  recommendedProductLevel: recommendedProductLevel,
                  showRecommended: showRecommended
                }), /*#__PURE__*/_jsx("div", {
                  children: /*#__PURE__*/_jsx(FormattedMessage, {
                    message: ApiNameToProductTranslationKeyMap[apiName]
                  })
                }), /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
                  isLoading: !hasPricingAndPurchasingSucceeded || !hasProductsSucceeded,
                  heights: [20],
                  widths: [60],
                  className: "flex-column align-center",
                  Content: FeatureTablePriceDisplay,
                  contentProps: {
                    apiName: apiNameForPrice
                  }
                })]
              }, apiName);
            })]
          })
        }), /*#__PURE__*/_jsx(TBody, {
          children: sortedFeatures.map(feature => {
            return /*#__PURE__*/_jsx(FeatureTableRow, {
              highlightedProducts: highlightedProducts,
              feature: feature,
              productLine: productLine
            }, feature.featureId);
          })
        })]
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "text-center justify-center align-center",
      children: /*#__PURE__*/_jsxs(UIButton, {
        use: "transparent",
        onClick: handleExpandClick,
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: expanded ? 'featureTable.collapse' : 'featureTable.expand'
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: expanded ? 'up' : 'down',
          size: "xxs",
          color: EERIE
        })]
      })
    })]
  });
};
export default FeatureTable;